import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useBooking, CustomerInfo } from "../context/BookingContext";
import { useContent } from "../context/ContentProvider";
import {
  ArrowRight,
  ArrowLeft,
  Shield,
  CreditCard,
  Landmark,
} from "lucide-react";
import { bookingService } from "../services/bookingService";

interface FormErrors {
  [key: string]: string;
}

const CustomerStep: React.FC = () => {
  const navigate = useNavigate();
  const {
    formData,
    selectedRooms,
    selectedExtras,
    customerInfo,
    updateCustomerInfo,
    updateFormData,
    totalPrice,
    calculateTotalPrice,
    setConfirmationNumber,
    hotelSlug,
    setCurrentStep,
  } = useBooking();
  const { content } = useContent();

  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!formData.checkIn || !formData.checkOut || selectedRooms.length === 0) {
      navigate("../rooms");
      return;
    }

    calculateTotalPrice();
  }, [formData, navigate, selectedRooms, calculateTotalPrice]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;

    if (!customerInfo.firstName) {
      newErrors.firstName = "Prénom requis";
    }

    if (!customerInfo.lastName) {
      newErrors.lastName = "Nom requis";
    }

    if (!customerInfo.email) {
      newErrors.email = "Email requis";
    } else if (!emailRegex.test(customerInfo.email)) {
      newErrors.email = "Format d'email invalide";
    }

    if (!customerInfo.phone) {
      newErrors.phone = "Téléphone requis";
    } else if (!phoneRegex.test(customerInfo.phone)) {
      newErrors.phone = "Format de téléphone invalide";
    }

    if (!customerInfo.address) {
      newErrors.address = "Adresse requise";
    }

    if (!customerInfo.city) {
      newErrors.city = "Ville requise";
    }

    if (!customerInfo.zipCode) {
      newErrors.zipCode = "Code postal requis";
    }

    if (!customerInfo.country) {
      newErrors.country = "Pays requis";
    }

    if (!customerInfo.paymentMethod) {
      newErrors.paymentMethod = "Méthode de paiement requise";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof CustomerInfo, value: string) => {
    updateCustomerInfo(field, value);

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleBack = () => {
    navigate("../extras");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("🎯 Starting booking submission...");

    if (!validateForm()) {
      console.log("❌ Form validation failed");
      const firstError = document.querySelector(".text-red-500");
      if (firstError) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setIsLoading(true);
    console.log("📝 Creating booking with data:", {
      checkIn: formData.checkIn,
      checkOut: formData.checkOut,
      rooms: selectedRooms,
      extras: selectedExtras,
      customerInfo: { ...customerInfo, email: "***@***.***" },
    });

    // Update formData with customerInfo for consistency
    updateFormData(
      "guestName",
      `${customerInfo.firstName} ${customerInfo.lastName}`
    );
    updateFormData("guestEmail", customerInfo.email);
    updateFormData("guestPhone", customerInfo.phone);
    updateFormData("specialRequests", customerInfo.specialRequests);
    updateFormData("guestAddress", customerInfo.address);
    updateFormData("guestCity", customerInfo.city);
    updateFormData("guestZipCode", customerInfo.zipCode);
    updateFormData("guestCountry", customerInfo.country);

    try {
      const bookingData = {
        room_id: selectedRooms[0].id,
        check_in: formData.checkIn!,
        check_out: formData.checkOut!,
        guest_name: `${customerInfo.firstName} ${customerInfo.lastName}`,
        guest_email: customerInfo.email,
        guest_phone: customerInfo.phone,
        adults: parseInt(formData.adults),
        children: parseInt(formData.children),
        rooms_count: parseInt(formData.rooms), // Add rooms count
        total_price: totalPrice,
        special_requests: customerInfo.specialRequests,
        hotelSlug: hotelSlug, // Add hotelSlug from context
        extras_info: selectedExtras.filter((extra) => extra.selected),
        payment_info: {
          method: customerInfo.paymentMethod,
          status: "pending",
        },
        // Add address fields
        guest_address: customerInfo.address,
        guest_city: customerInfo.city,
        guest_zipCode: customerInfo.zipCode,
        guest_country: customerInfo.country,
      };

      console.log("🚀 Calling bookingService.createBooking...");
      const booking = await bookingService.createBooking(bookingData);
      console.log("✅ Booking created successfully:", booking.id);

      setConfirmationNumber(booking.confirmation_number);

      setIsLoading(false);
      setCurrentStep(5);
    } catch (error) {
      console.error("❌ Error creating booking:", error);
      setIsLoading(false);
      alert(
        (error as Error).message || "Une erreur est survenue lors de la création de votre réservation. Veuillez réessayer."
      );
    }
  };

  const nights =
    formData.checkIn && formData.checkOut
      ? Math.ceil(
          (formData.checkOut.getTime() - formData.checkIn.getTime()) /
            (1000 * 60 * 60 * 24)
        )
      : 0;

  const formatDate = (date: Date | undefined) => {
    if (!date) return "";
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="py-8 px-4 bg-[#F5F3F0]">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl font-semibold text-[#67273f] mb-6">
          {content.personalInfoTitle || "Informations personnelles"}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Form Section */}
          <div className="md:col-span-2">
            <form
              onSubmit={handleSubmit}
              className="bg-white p-6 rounded-[10px]  shadow-md mb-6"
            >
              {/* Personal Information */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-[#67273f] mb-4">
                  Coordonnées
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prénom
                    </label>
                    <input
                      type="text"
                      value={customerInfo.firstName}
                      onChange={(e) =>
                        handleInputChange("firstName", e.target.value)
                      }
                      className={`w-full px-4 py-2 rounded-[10px]  border ${
                        errors.firstName ? "border-red-500" : "border-gray-300"
                      } focus:outline-none focus:ring-1 focus:ring-[#87764e]`}
                    />
                    {errors.firstName && (
                      <p className="text-sm text-red-500 mt-1">
                        {errors.firstName}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom
                    </label>
                    <input
                      type="text"
                      value={customerInfo.lastName}
                      onChange={(e) =>
                        handleInputChange("lastName", e.target.value)
                      }
                      className={`w-full px-4 py-2 rounded-[10px]  border ${
                        errors.lastName ? "border-red-500" : "border-gray-300"
                      } focus:outline-none focus:ring-1 focus:ring-[#87764e]`}
                    />
                    {errors.lastName && (
                      <p className="text-sm text-red-500 mt-1">
                        {errors.lastName}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={customerInfo.email}
                      onChange={(e) =>
                        handleInputChange("email", e.target.value)
                      }
                      className={`w-full px-4 py-2 rounded-[10px]  border ${
                        errors.email ? "border-red-500" : "border-gray-300"
                      } focus:outline-none focus:ring-1 focus:ring-[#87764e]`}
                    />
                    {errors.email && (
                      <p className="text-sm text-red-500 mt-1">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      value={customerInfo.phone}
                      onChange={(e) =>
                        handleInputChange("phone", e.target.value)
                      }
                      className={`w-full px-4 py-2 rounded-[10px]  border ${
                        errors.phone ? "border-red-500" : "border-gray-300"
                      } focus:outline-none focus:ring-1 focus:ring-[#87764e]`}
                    />
                    {errors.phone && (
                      <p className="text-sm text-red-500 mt-1">
                        {errors.phone}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-[#67273f] mb-4">
                  Adresse
                </h3>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adresse
                  </label>
                  <input
                    type="text"
                    value={customerInfo.address}
                    onChange={(e) =>
                      handleInputChange("address", e.target.value)
                    }
                    className={`w-full px-4 py-2 rounded-[10px]  border ${
                      errors.address ? "border-red-500" : "border-gray-300"
                    } focus:outline-none focus:ring-1 focus:ring-[#87764e]`}
                  />
                  {errors.address && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.address}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Code postal
                    </label>
                    <input
                      type="text"
                      value={customerInfo.zipCode}
                      onChange={(e) =>
                        handleInputChange("zipCode", e.target.value)
                      }
                      className={`w-full px-4 py-2 rounded-[10px]  border ${
                        errors.zipCode ? "border-red-500" : "border-gray-300"
                      } focus:outline-none focus:ring-1 focus:ring-[#87764e]`}
                    />
                    {errors.zipCode && (
                      <p className="text-sm text-red-500 mt-1">
                        {errors.zipCode}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ville
                    </label>
                    <input
                      type="text"
                      value={customerInfo.city}
                      onChange={(e) =>
                        handleInputChange("city", e.target.value)
                      }
                      className={`w-full px-4 py-2 rounded-[10px]  border ${
                        errors.city ? "border-red-500" : "border-gray-300"
                      } focus:outline-none focus:ring-1 focus:ring-[#87764e]`}
                    />
                    {errors.city && (
                      <p className="text-sm text-red-500 mt-1">{errors.city}</p>
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pays
                  </label>
                  <select
                    value={customerInfo.country}
                    onChange={(e) =>
                      handleInputChange("country", e.target.value)
                    }
                    className={`w-full px-4 py-2 rounded-[10px]  border ${
                      errors.country ? "border-red-500" : "border-gray-300"
                    } focus:outline-none focus:ring-1 focus:ring-[#87764e]`}
                  >
                    <option value="CH">Suisse</option>
                    <option value="FR">France</option>
                    <option value="DE">Allemagne</option>
                    <option value="IT">Italie</option>
                    <option value="AT">Autriche</option>
                    <option value="BE">Belgique</option>
                    <option value="ES">Espagne</option>
                    <option value="GB">Royaume-Uni</option>
                    <option value="US">États-Unis</option>
                    <option value="CA">Canada</option>
                    <option value="Other">Autre</option>
                  </select>
                  {errors.country && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.country}
                    </p>
                  )}
                </div>
              </div>

              {/* Special Requests */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-[#67273f] mb-4">
                  Demandes spéciales
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Demandes particulières (optionnel)
                  </label>
                  <textarea
                    value={customerInfo.specialRequests}
                    onChange={(e) =>
                      handleInputChange("specialRequests", e.target.value)
                    }
                    rows={3}
                    className="w-full px-4 py-2 rounded-[10px]  border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#87764e]"
                    placeholder="Informez-nous de vos demandes spéciales (ex: allergies, heure d'arrivée, etc.)"
                  />
                </div>
              </div>

              {/* Payment Method */}
              <div>
                <h3 className="text-lg font-semibold text-[#67273f] mb-4">
                  Méthode de paiement
                </h3>

                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="credit-card"
                      value="credit-card"
                      checked={customerInfo.paymentMethod === "credit-card"}
                      onChange={() =>
                        handleInputChange("paymentMethod", "credit-card")
                      }
                      className="w-4 h-4 text-[#87764e]"
                    />
                    <label
                      htmlFor="credit-card"
                      className="ml-2 flex items-center"
                    >
                      <CreditCard className="w-5 h-5 mr-2 text-gray-600" />
                      <span>Carte de crédit</span>
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="bank-transfer"
                      value="bank-transfer"
                      checked={customerInfo.paymentMethod === "bank-transfer"}
                      onChange={() =>
                        handleInputChange("paymentMethod", "bank-transfer")
                      }
                      className="w-4 h-4 text-[#87764e]"
                    />
                    <label
                      htmlFor="bank-transfer"
                      className="ml-2 flex items-center"
                    >
                      <Landmark className="w-5 h-5 mr-2 text-gray-600" />
                      <span>Virement bancaire</span>
                    </label>
                  </div>
                </div>
                {errors.paymentMethod && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.paymentMethod}
                  </p>
                )}

                <div className="mt-4 flex items-start">
                  <Shield className="w-5 h-5 mr-2 text-[#67273f] flex-shrink-0 mt-1" />
                  <p className="text-sm text-gray-600">
                    Vos informations de paiement sont sécurisées. Aucun montant
                    ne sera débité avant votre séjour.
                  </p>
                </div>
              </div>
            </form>
          </div>

          {/* Summary Section */}
          <div className="md:col-span-1">
            <div className="bg-white p-6 rounded-[10px]  shadow-md sticky top-8">
              <h3 className="text-lg font-semibold text-[#67273f] mb-4">
                Récapitulatif
              </h3>

              <div className="space-y-4 mb-6">
                <div className="pb-3 border-b border-gray-200">
                  <p className="text-sm font-medium">Séjour</p>
                  <p className="text-sm text-gray-600">
                    Du {formatDate(formData.checkIn)} au{" "}
                    {formatDate(formData.checkOut)}
                  </p>
                  <p className="text-sm text-gray-600">
                    {nights} {nights > 1 ? "nuits" : "nuit"}
                  </p>
                </div>

                <div className="pb-3 border-b border-gray-200">
                  <p className="text-sm font-medium">Chambres</p>
                  {selectedRooms.map((room) => {
                    const pricePerNight = room.price * room.count;
                    const totalRoomPrice = pricePerNight * nights;
                    
                    return (
                      <div key={room.id} className="mb-3 last:mb-0">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium">
                            {room.count}x {room.type}
                          </span>
                          <span className="font-medium">{totalRoomPrice} €</span>
                        </div>
                        
                        {/* Per night breakdown */}
                        <div className="text-xs text-gray-500 mt-1 ml-2">
                          <div className="flex justify-between">
                            <span>{pricePerNight} € par nuit × {nights} nuit{nights > 1 ? 's' : ''}</span>
                            <span>{totalRoomPrice} €</span>
                          </div>
                          {room.count > 1 && (
                            <div className="text-xs text-gray-400 mt-0.5">
                              ({room.price} € par chambre × {room.count} chambre{room.count > 1 ? 's' : ''})
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {selectedExtras.some((extra) => extra.selected) && (
                  <div className="pb-3 border-b border-gray-200">
                    <p className="text-sm font-medium">Services additionnels</p>
                    {selectedExtras
                      .filter((extra) => extra.selected)
                      .map((extra) => (
                        <div
                          key={extra.id}
                          className="flex justify-between text-sm text-gray-600"
                        >
                          <span>{extra.name}</span>
                          <span>{extra.price} €</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              <div className="flex justify-between font-bold text-lg text-[#67273f] mb-6">
                <span>Total:</span>
                <span>{totalPrice} €</span>
              </div>

              <div className="space-y-4">
                <button
                  onClick={handleBack}
                  className="w-full py-2 border-2 border-[#67273f] text-[#67273f] font-semibold rounded-[10px]  transition-all duration-200 hover:bg-[#67273f] hover:text-white flex items-center justify-center"
                >
                  <ArrowLeft className="mr-2 w-5 h-5" />
                  Retour
                </button>

                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="w-full py-3 bg-[#87764e] hover:bg-[#7a6b47] text-white font-semibold rounded-[10px]  transition-all duration-200 flex items-center justify-center disabled:opacity-70"
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Traitement...
                    </>
                  ) : (
                    <>
                      Confirmer la réservation
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerStep;
